import React from 'react';
import '../../../styles.scss';

import { fetchSigned, checkForCompletion } from "../../../shared/Utilities.js"
import CountryReportSpecification from "./CountryReportSpecification.js"
import CountryReportOverview from "./CountryReportOverview.js"
import CountryReportResults from "./CountryReportResults.js"
import { configData } from '../../../config.js';
import {
  OwcTypography,  OwcExpandable,
} from '@one/react';

/**
 * @copyright Roche 2025
 * @author Nick Draper
 */

/**
 * Specific control for querying and displaying the Country report
 */
class CountryReport extends React.Component {

  constructor(props) {
    super(props);
    this.cancelRequested = React.createRef();
    this.cancelRequested.current = false;

    this.state = {
      reportResults: null,
      url: `${configData.DATA_PRODUCT_URL}/data/country_report/`,
      currentView: "Results",
      userMessage: null,
      messageExpanded: true,
      label:{},
    }
  }

  static getDataProductName(){
    return "Country Report";
  }

  componentDidMount() {
    const baseUrl = this.state.url;
    if (baseUrl !== null) {
      const labelUrl = baseUrl + "label/";
      fetchSigned(labelUrl)
        .then(async response => {
          let responseDict = {};

          if (response !== undefined) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
              responseDict = await response.json();
            } else {
              console.log(response)
            }
          }
          this.setState({ label: responseDict });
        })
        .catch(error => {
          this.setState({ responseText: error.toString() });
          console.error('There was an error!', error);
        });
    }
  }

  /**
   * Signs and sumbits the request and populates the response
   */
  handleSubmitClick = (parameters) => {
    this.cancelRequested.current = false;
    this.setState({
      userMessage: this.formatUserMessage("Generating data product...", "warning"),
    });
    
    let urlToFetch = this.state.url;
    if ((parameters !== null) && (parameters !== undefined)) {
      urlToFetch += parameters;
    }

    if ((this.props.userName !== null) && (this.props.userName !== undefined)) {
      // Add user data for DP tracking
      urlToFetch += ([null, undefined, ""].includes(parameters) ? "?" : "&");
      urlToFetch += `userName=${this.props.userName}`;
    }

    console.log(`Fetching URL: ${urlToFetch}`);
    fetchSigned(urlToFetch)
      .then(async response => {
        let responseDict = {};
        if (response === undefined || !response.ok) {
          this.setState({
            userMessage: this.formatUserMessage("There was an error requesting the data product")
          });
        } else {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            responseDict = await response.json();
            if (this.cancelRequested.current === false) {
              this.handleDataProductWait(responseDict);
            }
          } else {
            this.setState({
              userMessage: this.formatUserMessage(`The request to generate the data product failed with the message '${await response.text()}'`),
            });
          }
        }   
      })
      .catch(error => {
        this.setState({ userMessage: this.formatUserMessage(error.toString()) });
        console.error('There was an error!', error);
      });
  }

  async handleDataProductWait(responseDict) {
    console.log("Generating Country Report");
    const requestResult = await checkForCompletion(
      `${this.state.url}status/${responseDict}`, this.cancelRequested,
    );

    if (requestResult.status === "SUCCEEDED") {
      this.getResults(requestResult.resultUrl);
    } else {
      this.setState(
        {userMessage: this.formatUserMessage(requestResult.responseText)}
      );
    }
  }

  /**
   * Loads the data product and displays it
   * @param {*} resultsURL 
   */
  getResults(resultsURL){
    console.log(`Reading Data Product from URL ${resultsURL}`);
    fetch(resultsURL,{
      method: "GET",
      headers: {'Accept': '*/*'},
      mode: "cors",
      cache: "no-cache",
      referrer: "client",
    })
    .then(async response => {
      if (response === undefined || !response.ok) {
        this.setState({userMessage: this.formatUserMessage(`There was an error (${response.status}) retrieving from the url ` + resultsURL + response.statusText)});
      } else {
        let reportResults = "";
        reportResults = await response.json();
        console.log(`Report: ${JSON.stringify(reportResults)}`);
        this.setState({reportResults: reportResults,
                       userMessage: null,
        }); 
      }
    })
    .catch(error => {
      this.setState({ userMessage: this.formatUserMessage(error.toString()) });
      console.error('There was an error!', error);
    });
  }

  handleRequestCancel = () => {
    this.cancelRequested.current = true;
    this.setState({ userMessage: {error:"Request was cancelled."} });
  }

  formatUserMessage(message, messageType = "error") {
    return { 
      message: message,
      type: messageType,
    };
  }

  /**
   * Download a file of the response
   */
  downloadFile() {
    let extension = ".json";
    let contentType = "application/json";
    const fileName = CountryReport.getDataProductName().replaceAll(" ","-") + extension;
    const blob = new Blob([JSON.stringify(this.state.reportResults)], { type: contentType });
    this.saveFile(blob, fileName);
  }


  /**
   * An internal method to allow instanteous downloads
   * @param {*} blob the data to download
   * @param {*} filename the filename to use
   */
  saveFile = async (blob, filename) => {
    const a = document.createElement('a');
    a.download = filename;
    a.href = URL.createObjectURL(blob);
    a.addEventListener('click', (e) => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };


  renderUserMessage() {
    const message = this.state.userMessage;
    if (message !== null) {
      return(
        <div style={{ marginLeft: "2em", marginRight: "2em"}}>
                  <OwcExpandable roundedControl variant="transparent"
                      expanded={this.state.messageExpanded}
                      onExpandedChange={(ev) => { this.setState({ messageExpanded: ev.detail }) }}>
                    <span slot="title">
                      <OwcTypography variant="title6">
                        {message.type === "error"? "An error occurred": "Process Message"}
                      </OwcTypography>
                    </span>
                    <span slot="content">
                      <OwcTypography variant="title7" style={{ marginLeft: "2em", marginRight: "2em"}}>
                        {message.message}
                      </OwcTypography>
                    </span>
                  </OwcExpandable>
                </div>
      );
    } else {
      return("");
    }
  }

  /**
  * Renders the request section for the Data Product
  * @returns formatted JSX
  */
  render() {
    return (
      <div style={{
            display:"flex", flexDirection:"column",
            alignItems:"left"
          }}>
        <CountryReportSpecification 
          key="CountryReportSpecification"
          label={this.state.label}
          cancelRequested={this.cancelRequested}
          onSubmit={this.handleSubmitClick}
          onCancel={this.handleRequestCancel}
        />
        {this.state.userMessage === null && this.state.reportResults
        ?
          <>
            <CountryReportOverview key="CountryReportOverview" report={this.state.reportResults}/>
            <CountryReportResults key="CountryReportResults" report={this.state.reportResults} onDownloadClick={()=>this.downloadFile()}/>
          </>
        :
          this.renderUserMessage()
        }
      </div>
    );
  }
}

export default CountryReport;
